import React, {
  useEffect, useImperativeHandle, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { EventoServer } from '@cgn/framework-ui-components/v1';
import { messaggiServer } from '../../Exports/iframeComunication';

class EventiServer {
  subscribers = {};

  subscribe(eventName, callback) {
    if (!this.subscribers[eventName]) {
      this.subscribers[eventName] = [];
    }
    this.subscribers[eventName].push(callback);

    return () => {
      this.unsubscribe(eventName, callback);
    };
  }

  unsubscribe(eventName, callback) {
    if (!this.subscribers[eventName]) {
      return;
    }
    this.subscribers[eventName] = this.subscribers[eventName].filter((cb) => cb !== callback);
  }

  fireEvent(eventName, event) {
    if (!this.subscribers[eventName]) {
      return;
    }
    this.subscribers[eventName].forEach((cb) => cb(event));
  }
}

export const EventiServerContext = React.createContext({
  subscribe: (eventName, callback) => () => () => { },
  unsubscribe: (eventName, callback) => { },
  fireEvent: (eventName, event) => { },
});

export const EventiServerProvider = React.forwardRef(({ children }, ref) => {
  const eventiServerContextValue = useMemo(() => new EventiServer(), []);

  useImperativeHandle(ref, () => ({
    fireEvent: (eventName, event) => {
      eventiServerContextValue.fireEvent(eventName, event);
    },
  }));

  return (
    <EventiServerContext.Provider value={eventiServerContextValue}>
      {children}
    </EventiServerContext.Provider>
  );
});

EventiServerProvider.displayName = 'EventiServerProvider';
EventiServerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function SignalREventiServerProvider({ children }) {
  const eventiServerProviderRef = useRef();

  useEffect(() => {
    const callbacks = messaggiServer.map((evento) => {
      const callback = (event) => {
        eventiServerProviderRef.current.fireEvent(evento, JSON.parse(event));
      };
      EventoServer.subscribe(evento, callback);
      return { evento, callback };
    });

    return () => {
      callbacks.forEach(({ evento, callback }) => {
        EventoServer.unsubscribe(evento, callback);
      });
    };
  }, []);

  return (
    <EventiServerProvider ref={eventiServerProviderRef}>
      {children}
    </EventiServerProvider>
  );
}

SignalREventiServerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function IFrameEventiServerProvider({ url, children }) {
  const eventiServerProviderRef = useRef();

  useEffect(() => {
    window.onmessage = (event) => {
      if (event.data && event.data.nomeEvento && event.data.evento) {
        eventiServerProviderRef.current.fireEvent(event.data.nomeEvento, event.data.evento);
      }
    };
  }, []);

  useEffect(() => {
    if (url) {
      EventoServer.connettiSignalR(window.IdUtente, url);

      return () => {
        EventoServer.disconnettiSignalR();
      };
    }

    return undefined;
  }, [url]);

  return (
    <EventiServerProvider ref={eventiServerProviderRef}>
      {children}
    </EventiServerProvider>
  );
}

IFrameEventiServerProvider.propTypes = {
  url: PropTypes.string,
  children: PropTypes.node.isRequired,
};

IFrameEventiServerProvider.defaultProps = {
  url: undefined,
};

export function withEventiServer(Component) {
  const Consumer = React.forwardRef((props, ref) => (
    <EventiServerContext.Consumer>
      {(context) => <Component {...props} ref={ref} eventiServerContext={context} />}
    </EventiServerContext.Consumer>
  ));

  Consumer.displayName = `withEventiServer(${Component.displayName || Component.name})`;

  return Consumer;
}
